import { useMemo } from 'react'
import { useGetServicetypesServiceTypeId } from '@/api/generated/service-type/service-type'
import { getViewServiceErrorMessage } from './getViewServiceErrorMessage'
import { getServiceApiErrorBody } from '@/hooks/getServiceApiErrorBody'

/**
 * Reads a single service type by id. Replaces the `getService` thunk + GET_SERVICE_INFO* flow
 * and the `viewService.data[id]` reducer storage.
 *
 * The old reducer keyed responses by id (`viewService.data[serviceTypeId]`). To keep
 * viewservice.jsx's `savedData[id]` reads unchanged, this returns a `savedData` map with the
 * fetched contract under the requested id (empty object while loading / on error, matching the
 * old `data: {}` initial state).
 *
 * The old page derived its inline error from the GET_SERVICE_INFO_ERROR listener branch; the
 * message string is exposed here so the page can feed it into its local `setError`.
 */
export const useService = (id: string | null) => {
  const query = useGetServicetypesServiceTypeId(id ?? '', {
    query: { enabled: !!id },
  })

  const savedData = useMemo(() => (id && query.data ? { [id]: query.data } : {}), [id, query.data])

  const errorMessage = useMemo(
    () => (query.error ? getViewServiceErrorMessage(getServiceApiErrorBody(query.error)) : ''),
    [query.error],
  )

  return { ...query, savedData, errorMessage }
}
